import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toFaDigits } from '@/lib/format'

const CONTROL = 'w-full rounded-md border border-input bg-card text-body text-foreground placeholder:text-subtle transition-[border-color,box-shadow,background-color] duration-150 hover:border-subtle/60 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-primary/15 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/15'

interface FieldProps {
    id: string
    label: string
    required?: boolean
    hint?: string
    error?: string
    counter?: {
        value: number
        max: number
    }
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({
    id,
    label,
    required = false,
    hint,
    error,
    counter,
    className,
    children
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <div className='flex items-center justify-between gap-3'>
                <label htmlFor={id} className='text-sm font-semibold text-navy'>
                    {label}
                    {required && <span className='ms-1 text-destructive' aria-hidden='true'>*</span>}
                </label>

                {counter && (
                    <span
                        className={cn('text-caption tabular-nums', counter.value > counter.max ? 'text-destructive' : 'text-muted-foreground')}
                        aria-live='polite'
                    >
                        {toFaDigits(counter.value)} / {toFaDigits(counter.max)}
                    </span>
                )}
            </div>

            {children}

            {error ? (
                <p id={`${id}-error`} className='text-small text-destructive' role='alert'>
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className='text-small text-muted-foreground'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    invalid?: boolean
}

export const Input: FC<InputProps> = ({ className, invalid, ...props }): ReactNode => {
    return (
        <input
            aria-invalid={invalid || undefined}
            className={cn(CONTROL, 'h-11 px-3.5', className)}
            {...props}
        />
    )
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    invalid?: boolean
}

export const Textarea: FC<TextareaProps> = ({ className, invalid, ...props }): ReactNode => {
    return (
        <textarea
            aria-invalid={invalid || undefined}
            className={cn(CONTROL, 'min-h-28 px-3.5 py-3 leading-8', className)}
            {...props}
        />
    )
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    invalid?: boolean
}

export const Select: FC<SelectProps> = ({ className, invalid, children, ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select
                aria-invalid={invalid || undefined}
                className={cn(CONTROL, 'h-11 cursor-pointer appearance-none ps-3.5 pe-10', className)}
                {...props}
            >
                {children}
            </select>

            <ChevronDown className='pointer-events-none absolute end-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle' aria-hidden='true' />
        </div>
    )
}

interface CheckProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label: ReactNode
    description?: string
    count?: number
    type?: 'checkbox' | 'radio'
}

export const Check: FC<CheckProps> = ({
    label,
    description,
    count,
    type = 'checkbox',
    className,
    id,
    ...props
}): ReactNode => {
    return (
        <label
            htmlFor={id}
            className={cn(
                'group flex cursor-pointer items-start gap-2.5 rounded-md py-1.5 text-sm text-foreground has-disabled:cursor-not-allowed has-disabled:opacity-60',
                className
            )}
        >
            <input
                id={id}
                type={type}
                className='mt-1 size-4 shrink-0 cursor-pointer accent-primary-strong'
                {...props}
            />

            <span className='flex min-w-0 flex-1 flex-col'>
                <span className='flex items-center justify-between gap-2'>
                    <span className='group-hover:text-navy'>{label}</span>

                    {count !== undefined && (
                        <span className='text-caption tabular-nums text-muted-foreground'>
                            {toFaDigits(count)}
                        </span>
                    )}
                </span>

                {description && (
                    <span className='text-small text-muted-foreground'>
                        {description}
                    </span>
                )}
            </span>
        </label>
    )
}
